/**
 * Per-step validation for the Infrastructure flow. Pure functions of the wizard data: no React, no fetching.
 * A step is valid when its list of problems is empty. "Next" stays disabled until it is.
 */
import { CUSTOMER_CODE_IN_TEXT } from '@/data/customerCode'
import { environmentTypeOf } from '@/data/environmentTypes'
import { licenseFieldsFor } from '@/data/licenses'
import { hasProductLine, isProcessPro } from '@/data/productLines'
import { RDS_VM_COUNT_MAX, RDS_VM_COUNT_MIN, isRdsVmCount } from '@/data/sizeCatalog'
import { RESERVED_SQL_INSTANCE_NAMES, SQL_INSTANCE_NAME_MAX, SQL_INSTANCE_QUESTION } from '@/data/sqlInstance'
import { componentsFor } from '@/data/traverseComponents'
import type { DataDisk, IisServer, NetworkChoice } from '@/types/infrastructure'
import type { DedicatedSqlVmData, IisAddOnData, InfraWizardData } from '@/stores/infrastructureWizard'
import { asksRdsVmCount, effectiveServerMode, publicIpCount, rdsVmCount } from '@/stores/infrastructureWizard'
import { passwordProblem, validatePaas } from '@/utils/infraPaasFields'
import { validateSqlStorage } from '@/utils/sqlStorage'
import {
  SERVER_ROLE_LABELS,
  hasNamingRule,
  isValidCustomerNumber,
  isValidHostName,
  requiresCustomerCode,
  sequenceKey,
  serverNameFromSequence,
  serverNameSeries,
  toAzureSqlServerName,
  type ServerRole,
} from '@/utils/naming'

export interface InfraContext {
  /** IP addresses the RDS server needs in its subnet: one per RDS VM. Defaults to the number of RDS VMs of the data (`rdsVmCount`). */
  requiredIps?: number
  /** VM sizes no series of which can be built in the chosen region (`vmSizeProblems`). The Servers step does not continue until they are gone. */
  vmSizeProblems?: readonly string[]
  /**
   * What stops the Servers step while a public IP is answered Yes: the quota check is being asked, says the quota is not there, says Azure could not
   * be read, or could not be asked at all (`publicIpProblems`). Empty when no public IP is wanted or the quota is there.
   */
  publicIpProblems?: readonly string[]
}

export interface InfraValidation {
  location: string[]
  level: string[]
  servers: string[]
  addons: string[]
  valid: boolean
}

// ─── Server names ────────────────────────────────────────────────────────────

export type ResolvedSqlServerName = { ok: true; name: string; note: string } | { ok: false; problem: string }

type NameData = Pick<InfraWizardData, 'product' | 'environment' | 'serverSequences' | 'manualSqlServerName'>

/**
 * A server's name, to display and submit, from the naming module (utils/naming.ts) and the next number the service gave for its
 * series. It is a PREVIEW: the pipeline reserves the real name just before Terraform runs. `style` is 'host' for a VM and 'azure' for an
 * Azure SQL (PaaS) server, which Azure requires in lower case. A product with no naming rule has no generated names: its SQL server name
 * is typed. `count` is how many consecutive names a role needs (the RDS VMs of the RDS server); the first is the server's name.
 */
export function resolveServerName(data: NameData, role: ServerRole, style: 'host' | 'azure' = 'host', count = 1): ResolvedSqlServerName & { names?: string[] } {
  const asStyle = (name: string, note: string): ResolvedSqlServerName => {
    if (style === 'host') return { ok: true, name, note }
    const azure = toAzureSqlServerName(name)
    return azure
      ? { ok: true, name: azure, note: `${note} Azure SQL server names are lower case, so it is used as ${azure}.` }
      : { ok: false, problem: `"${name}" cannot be used as an Azure SQL server name.` }
  }
  if (hasNamingRule(data.product)) {
    const environmentId = data.environment?.id ?? ''
    const series = serverNameSeries({ product: data.product, environmentId, role })
    if (!series.ok) return { ok: false, problem: series.problem }
    const sequence = data.serverSequences[sequenceKey(environmentId, series.series)]
    if (!sequence) return { ok: false, problem: `Working out the next free ${SERVER_ROLE_LABELS[role].toLowerCase()} name.` }
    const named = serverNameFromSequence(series.series, sequence, count)
    if (!named.ok) return named
    const first = asStyle(named.name, named.note)
    return first.ok ? { ...first, names: style === 'host' ? named.names : [first.name] } : first
  }
  if (role !== 'db') return { ok: false, problem: `No ${SERVER_ROLE_LABELS[role].toLowerCase()} naming rule is configured for this product.` }
  const manual = data.manualSqlServerName.trim()
  if (manual === '') return { ok: false, problem: 'No naming rule is configured for this product. Enter the SQL server name.' }
  if (!isValidHostName(manual)) return { ok: false, problem: 'Use up to 15 letters, digits or hyphens, without a leading or trailing hyphen.' }
  return asStyle(manual.toUpperCase(), 'Entered by hand: no naming rule is configured for this product.')
}

/** The SQL server's name: `resolveServerName` for the DB role. */
export const resolveSqlServerName = (data: NameData, style: 'host' | 'azure' = 'host'): ResolvedSqlServerName => resolveServerName(data, 'db', style)

/** A name the flow generates, and how it is written. */
export interface NamedRole {
  role: ServerRole
  style: 'host' | 'azure'
}

/** The DB server's name is lower case on the AppCentral level, where a dedicated one is an Azure SQL server. */
export const dbNameStyle = (level: InfraWizardData['level']): 'host' | 'azure' => (level === 'appCentral' ? 'azure' : 'host')

/**
 * The servers whose names the flow generates: each Dedicated server. The RDS server's name needs a naming rule; a product with
 * none types its SQL server name instead.
 */
export function serverRolesFor(data: Pick<InfraWizardData, 'product' | 'level' | 'rdsServer' | 'dbServer'>): NamedRole[] {
  if (!data.level) return []
  const roles: NamedRole[] = []
  if (effectiveServerMode(data.product, data.rdsServer.mode, 'rds') === 'dedicated' && hasNamingRule(data.product)) roles.push({ role: 'rds', style: 'host' })
  if (effectiveServerMode(data.product, data.dbServer.mode, 'db') === 'dedicated') roles.push({ role: 'db', style: dbNameStyle(data.level) })
  return roles
}

/** `count` is how many consecutive names the role needs: every one of them is checked (none may be too long). */
function nameProblems(data: InfraWizardData, role: NamedRole['role'], count = 1): string[] {
  return serverRolesFor(data)
    .filter((named) => named.role === role)
    .flatMap(({ style }) => {
      const resolved = resolveServerName(data, role, style, count)
      return resolved.ok ? [] : [resolved.problem]
    })
}

// ─── Field rules ─────────────────────────────────────────────────────────────

/**
 * A SQL Server named instance: up to SQL_INSTANCE_NAME_MAX (15) characters, starting with a letter or underscore, then letters,
 * digits, `_`, `$` or `#`. `existing` lists instances already on the server; the name must not be one of them (case-insensitive).
 */
export function validateSqlInstanceName(name: string, existing: readonly string[] = []): string | null {
  const value = name.trim()
  if (value === '') return 'SQL instance name is required.'
  if (value.length > SQL_INSTANCE_NAME_MAX) return `An instance name is at most ${SQL_INSTANCE_NAME_MAX} characters.`
  if (!/^[A-Za-z_][A-Za-z0-9_$#]*$/.test(value)) return 'Start with a letter or underscore; then use letters, digits, _, $ or #.'
  if (RESERVED_SQL_INSTANCE_NAMES.includes(value.toLowerCase())) return `"${value}" is reserved. Choose another name.`
  if (existing.some((instance) => instance.toLowerCase() === value.toLowerCase())) return `An instance named "${value}" already exists on this server.`
  return null
}

/** Problems for each disk, keyed by disk key. Drive letters and LUNs must be unique across the list. */
export function diskProblems(disks: readonly DataDisk[]): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  for (const disk of disks) {
    const problems: string[] = []
    if (!Number.isInteger(disk.sizeGb) || disk.sizeGb < 1 || disk.sizeGb > 32767) problems.push('Size must be a whole number from 1 to 32767 GB.')
    if (!Number.isInteger(disk.lun) || disk.lun < 0 || disk.lun > 63) problems.push('LUN must be a whole number from 0 to 63.')
    else if (disks.some((other) => other !== disk && other.lun === disk.lun)) problems.push(`LUN ${disk.lun} is used by another disk.`)
    if (!/^[E-Z]$/i.test(disk.driveLetter)) problems.push('Drive letter must be E to Z (C is the OS disk, D the temporary disk).')
    else if (disks.some((other) => other !== disk && other.driveLetter.toUpperCase() === disk.driveLetter.toUpperCase())) {
      problems.push(`Drive ${disk.driveLetter.toUpperCase()}: is used by another disk.`)
    }
    if (problems.length > 0) result[disk.key] = problems
  }
  return result
}

export function validateDataDisks(disks: readonly DataDisk[], minimum: number, label: string): string[] {
  const problems: string[] = []
  if (disks.length < minimum) problems.push(`${label}: add at least ${minimum === 1 ? 'one data disk' : `${minimum} data disks`}.`)
  for (const [, list] of Object.entries(diskProblems(disks))) problems.push(...list.map((p) => `${label}: ${p}`))
  return problems
}

/** The number of RDS VMs of a Dedicated RDS server that is asked for it must be a whole number from RDS_VM_COUNT_MIN to RDS_VM_COUNT_MAX. */
export function rdsVmCountProblems(data: Pick<InfraWizardData, 'product' | 'rdsServer'>): string[] {
  if (!asksRdsVmCount(data) || isRdsVmCount(data.rdsServer.count)) return []
  return [`RDS server: choose between ${RDS_VM_COUNT_MIN} and ${RDS_VM_COUNT_MAX} RDS VMs.`]
}

export function networkProblems(network: NetworkChoice, requiredIps: number, label: string): string[] {
  if (!network.vnet) return [`${label}: select a VNet.`]
  if (!network.subnet) return [`${label}: select a subnet.`]
  if (network.subnet.freeIps < requiredIps) {
    return [`${label}: subnet ${network.subnet.name} has ${network.subnet.freeIps} free IP${network.subnet.freeIps === 1 ? '' : 's'}, but ${requiredIps} ${requiredIps === 1 ? 'is' : 'are'} needed.`]
  }
  return []
}

function sqlAuthProblems(data: DedicatedSqlVmData): string[] {
  if (data.authType === 'windows') return []
  const problems: string[] = []
  if (!/^[A-Za-z][A-Za-z0-9_]{0,127}$/.test(data.adminUser)) problems.push('SQL admin username: start with a letter; use letters, digits and underscores.')
  if (data.adminPassword === '') problems.push('SQL admin password is required.')
  else {
    const problem = passwordProblem(data.adminPassword, data.adminUser)
    if (problem) problems.push(`SQL admin password: ${problem}`)
  }
  return problems
}

// ─── Steps ───────────────────────────────────────────────────────────────────

export function validateLocation(data: InfraWizardData): string[] {
  const problems: string[] = []
  if (!data.environment) problems.push('Select an environment.')
  else if (!data.region) problems.push('Select a region.')
  else if (!data.resourceGroup) problems.push('Select a resource group.')
  if (data.region) for (const issue of data.region.issues) problems.push(`${data.region.label} cannot be deployed to yet: ${issue.message}`)
  if (requiresCustomerCode(data.product) && !isValidCustomerNumber(data.customerNumber)) {
    problems.push(`Enter a ${CUSTOMER_CODE_IN_TEXT} (100–999) in the Environment section.`)
  }
  return problems
}

/** True when the product is asked for license fields: the product lines (Traverse, Process Pro). */
export const asksLicense = (product: InfraWizardData['product']): boolean => hasProductLine(product)

/**
 * The license fields of the chosen environment's type (data/licenses.ts), each required. Nothing until an environment is chosen. They are
 * asked on the Application (ERP) step, after the Infrastructure step, so the Infrastructure flow does not wait for them.
 */
export function licenseProblems(data: Pick<InfraWizardData, 'product' | 'environment' | 'license'>): string[] {
  if (!asksLicense(data.product)) return []
  return licenseFieldsFor(environmentTypeOf(data.environment))
    .filter((field) => data.license[field.key].trim() === '')
    .map((field) => `Enter the ${field.label} in the Application (ERP) step.`)
}

export function validateLevel(data: InfraWizardData): string[] {
  return data.level ? [] : ['Choose AppCentral level or Domain level.']
}

const where = (data: InfraWizardData) => `${data.resourceGroup?.name ?? ''} / ${data.region?.label ?? ''}`

/**
 * The RDS server: Existing (one chosen from the resource group) or Dedicated (how many RDS VMs, its network with room for all of them, its
 * data disks and its names, every one of the series).
 */
function rdsProblems(data: InfraWizardData, requiredIps: number): string[] {
  const mode = effectiveServerMode(data.product, data.rdsServer.mode, 'rds')
  if (!mode) return ['RDS server: choose Existing or Dedicated.']
  if (mode === 'existing') {
    if (data.existingRdsServers === null) return ['The existing RDS servers have not loaded yet.']
    if (data.existingRdsServers.length === 0) return [`No existing RDS server found in ${where(data)}.`]
    return data.rdsServer.existing ? [] : ['Select the existing RDS server.']
  }
  return [
    ...rdsVmCountProblems(data),
    ...networkProblems(data.rdsServer.network, requiredIps, 'RDS server network'),
    ...validateDataDisks(data.rdsServer.disks, 0, 'RDS server'),
    ...nameProblems(data, 'rds', rdsVmCount(data)),
  ]
}

/** The DB server: Existing (one chosen from the resource group), or Dedicated: a SQL VM (Domain) or an Azure SQL server (AppCentral). */
function dbProblems(data: InfraWizardData): string[] {
  const mode = effectiveServerMode(data.product, data.dbServer.mode, 'db')
  if (!mode) return ['DB server: choose Existing or Dedicated.']
  if (mode === 'existing') {
    if (data.existingDbServers === null) return ['The existing database servers have not loaded yet.']
    if (data.existingDbServers.length === 0) return [`No existing database server found in ${where(data)}.`]
    return data.dbServer.existing ? [] : ['Select the existing database server.']
  }
  const problems: string[] = []
  if (data.level === 'appCentral') {
    const { azureSql } = data.dbServer
    problems.push(...networkProblems(azureSql.network, 1, 'Azure SQL server network'))
    for (const message of Object.values(validatePaas(azureSql.paas))) problems.push(message)
  } else {
    const { sqlVm } = data.dbServer
    if (!sqlVm.image) problems.push('The latest SQL image has not loaded yet.')
    problems.push(...networkProblems(sqlVm.network, 1, 'DB server network'))
    problems.push(...validateSqlStorage(sqlVm.storage))
    problems.push(...sqlAuthProblems(sqlVm))
    if (sqlVm.collation.trim() === '') problems.push('SQL collation is required.')
  }
  problems.push(...nameProblems(data, 'db'))
  return problems
}

/**
 * The instances a new instance's name must not match: the chosen existing server's, where they are fetched (not on the AppCentral
 * level, where they are not looked up). A dedicated server has none yet.
 */
export function takenInstanceNames(data: Pick<InfraWizardData, 'product' | 'level' | 'dbServer'>): readonly string[] {
  if (data.level !== 'domain' || effectiveServerMode(data.product, data.dbServer.mode, 'db') !== 'existing') return []
  return data.dbServer.existing?.instances ?? []
}

/** The one SQL instance question, for every product, level and DB server choice: Yes asks for the name, No needs nothing. */
export function sqlInstanceProblems(data: InfraWizardData): string[] {
  if (data.sqlInstance.required === null) return [`Answer "${SQL_INSTANCE_QUESTION}"`]
  if (!data.sqlInstance.required) return []
  const problem = validateSqlInstanceName(data.sqlInstance.instanceName, takenInstanceNames(data))
  return problem ? [problem] : []
}

export function validateServers(data: InfraWizardData, context: InfraContext = {}): string[] {
  if (!data.level) return ['Choose the level first.']
  return [
    ...rdsProblems(data, context.requiredIps ?? rdsVmCount(data)),
    ...dbProblems(data),
    ...sqlInstanceProblems(data),
    ...(context.vmSizeProblems ?? []),
    // Only while a public IP is wanted: with every answer No there is nothing to check, whatever an earlier answer was.
    ...(publicIpCount(data) > 0 ? (context.publicIpProblems ?? []) : []),
  ]
}

/** Customer Portal or a Traverse component: only a Yes needs an IIS server, and it must be one that exists. Nothing exists is a reason, not a blank. */
function iisProblems(label: string, addOn: IisAddOnData, servers: readonly IisServer[] | null): string[] {
  if (!addOn.enabled || addOn.server) return []
  if (servers !== null && servers.length === 0) return [`${label}: no existing IIS server was found in this environment.`]
  return [`${label}: select the existing IIS server.`]
}

/** Advanced Analytics and Bartender are asked of Process Pro Global only. */
export const asksAnalyticsAddOns = (product: InfraWizardData['product']): boolean => isProcessPro(product)

export function validateAddOns(data: InfraWizardData): string[] {
  const problems: string[] = []
  if (asksAnalyticsAddOns(data.product)) {
    for (const [key, label] of [['advancedAnalytics', 'Advanced Analytics'], ['bartender', 'Bartender']] as const) {
      const addOn = data[key]
      if (!addOn.enabled) continue
      if (!addOn.goldenImage) problems.push(`${label}: select a golden image.`)
      problems.push(...networkProblems(addOn.network, 1, `${label} network`))
    }
  }
  if (hasProductLine(data.product)) {
    problems.push(...iisProblems('Customer Portal', data.customerPortal, data.existingIisServers))
    for (const component of componentsFor(data.product)) problems.push(...iisProblems(component.name, data[component.key], data.existingIisServers))
    if (data.linkVersion === null) problems.push('Choose the old link or the new link.')
  }
  return problems
}

/** Every step at once. `valid` is true only when all four are. */
export function validateInfrastructure(data: InfraWizardData, context: InfraContext = {}): InfraValidation {
  const location = validateLocation(data)
  const level = validateLevel(data)
  const servers = validateServers(data, context)
  const addons = validateAddOns(data)
  return { location, level, servers, addons, valid: [location, level, servers, addons].every((list) => list.length === 0) }
}

export const SUB_STEP_LABELS = { location: 'Location', level: 'Level', servers: 'Servers', addons: 'Add-ons' } as const
